/**
 * HiddenLine ("Mystery order", rules v2) view helpers, free of Phaser so they are unit-tested in
 * node: which ticket line is drawn face down, the reveal of a batch, and why the magnet is out.
 * Core decides every rule (`lineShown` is the single source of truth, a resumed round included);
 * these only read its compiled level, state and events.
 */
import { canMagnet, lineShown, type Round, type RoundEvent } from '@mp/core';

/** Does the level have a hidden line at all (the ticket skips every face-down pass otherwise)? */
export const hasHiddenLine = (r: Round): boolean => r.L.hiddenLine >= 0;

/** Ticket lines drawn face down now: the hidden line while core still hides it, else none. */
export function faceDownLines(r: Round): number[] {
  const out: number[] = [];
  for (let l = 0; l < r.L.nLines; l++) if (!lineShown(r, l)) out.push(l);
  return out;
}

/** The `lineRevealed` event of a batch, if any (at most one per round). */
export function revealOf(events: readonly RoundEvent[]): Extract<RoundEvent, { type: 'lineRevealed' }> | null {
  for (const e of events) if (e.type === 'lineRevealed') return e;
  return null;
}

/**
 * The batch's reveal came from serving the hidden line itself while it was face down (a triple of
 * its item popped before the revealer line was done): the `served` event names the still-hidden
 * line. The scene then flips the card first and runs that line's served tick after it.
 */
export function servedFaceDown(events: readonly RoundEvent[]): boolean {
  const rev = revealOf(events);
  return !!rev && events.some((e) => e.type === 'served' && e.line === rev.line);
}

/**
 * Is the magnet unavailable only because the one line it could serve is face down (it would have
 * a triple to take with the line turned up)? The button reads disabled like any other time core
 * says no; a tap on it then wiggles the face-down card as a hint. Reads a shallow copy with the
 * line shown; never mutates `r`.
 */
export function magnetHeldByHiddenLine(r: Round): boolean {
  return r.lineHidden && !canMagnet(r) && canMagnet({ ...r, lineHidden: false });
}
